import {
    Avatar, Backdrop, createTheme, Dialog, DialogContent,
    DialogContentText, DialogTitle, Grid, IconButton, List,
    ListItem, ListItemAvatar, ListItemText, MuiThemeProvider,
    TextField, Typography
} from '@material-ui/core';
import CircularProgress from '@material-ui/core/CircularProgress';
import { Add as AddIcon, Delete as DeleteIcon } from '@material-ui/icons';
import MUIDataTable from 'mui-datatables';
import { useSnackbar } from 'notistack';
import React, { useEffect, useRef, useState } from 'react';
import { useFieldArray } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import AlertDialog from '../../../components/ui/AlertDialog';
import ButtonUI from '../../../components/ui/ButtonUI';
import { useAuth } from '../../../hooks/auth';
import { MvcInvoice } from '../../../interfaces/Mvc';
import MvcApi from '../../../services/MvcApi';
import getDefaultGridOptions from '../../../utils/componentUtils';
import utilsDate from '../../../utils/Date';
import { useStyles } from '../Style';

type props =  any;

const MvcInvoiceList: React.FC<props> = ({ control, title, name, allowAdd, allowDelete, Mvc, onAdd  }) => {

    const classes = useStyles();
    const { t } = useTranslation("Mvc");
    const { t: tGeneral } = useTranslation("General");
    const { t: it } = useTranslation("Mvc");
    const { enqueueSnackbar } = useSnackbar();
    const { user, isCampoLargo } = useAuth();
    const [circularLoading, setCircularLoading] = useState(false);
    const [selectInvoiceType, setSelectInvoiceType] = useState(false);
    const [unknownTypeInvoice, setUnknownTypeInvoice] = useState<null | MvcInvoice>(null);
    const { fields: _fields, append, remove } = useFieldArray({ control, name: name });
    const fields = _fields as Record<string, any>[];
    const [invoiceTypes, setInvoiceTypes] = useState<string[]>([ 'V', 'E', 'A', 'G', 'I', 'M', 'P', 'R', 'T']);
    const refFieldInvoiceKey = useRef<HTMLInputElement>(null)
    
    useEffect(() => {
        //Campo Largo tem um tipo especifico de NF, que eh a nota Triangular (tipo=C)
        if (isCampoLargo())
            setInvoiceTypes([...invoiceTypes, 'C']);
        else
            setInvoiceTypes( [ ...invoiceTypes.filter((x: string) => x !== 'C') ] );
    }, [user.company]);

    const myTheme = createTheme({
        overrides: {
            MuiTableBody: {
                root: {
                    cursor: ''
                }
            }
        }
    })

    const columns = [
        {
            name: 'id',
            options: {
                display: false,
            }
        },
        {
            name: 'invoiceKey',
            label: String(t('invoiceKey')),
            options: {
                setCellProps: () => ({
                    align: "left",
                }),
            }
        },
        {
            name: 'invoiceNumber',
            label: String(t('invoiceNumber')),

            options: {
                sort: false,
                setCellHeaderProps: () => ({
                    align: "center",
                  }),
                setCellProps: () => ({
                    align: "center"
                })    
            }
        },
        {
            name: 'invoiceDate',
            label: String(t('invoiceDate')),
            options: {
                sort: false,
                setCellHeaderProps: () => ({
                    align: "center",
                  }),
                setCellProps: () => ({
                    align: "center"
                }),
                customBodyRender: (value: any, tableMeta: any, updateValue: any) => {
                    return utilsDate.DateShort(value);
                }
            }
        },
        {
            name: 'invoiceType',
            label: String(t('invoiceType')),
            options: {
                sort: false,
                setCellHeaderProps: () => ({
                    align: "center",
                  }),
                setCellProps: () => ({
                    align: "center"
                }),
                customBodyRender: (value: any, tableMeta: any, updateValue: any) => {
                    return it("invoiceType_" + value);
                }
            }
        },
        {
            name: "",
            options: {
                setCellProps: () => ({
                    align: "right"
                }),
                disableClick: true,
                sort: false,
                customBodyRender: (value: any, tableMeta: any, updateValue: any) => (
                    renderButtons(tableMeta.rowData[0], tableMeta.rowIndex)
                )
            }
        },
    ]

    const renderButtons = (id: any, rowIndex: any) => {
        //Se usuario acabou de incluir esta nota na lista, ela tem mvcid=0
        let justAdded = fields[rowIndex]['mvcId'] == 0;

        if ((allowAdd && justAdded) || allowDelete)
            return (
                <AlertDialog
                    dialogTitle={tGeneral("dialogDeleteTitle")}
                    dialogContentText={tGeneral("dialogDeleteContentText")}
                    cancelTextButton={tGeneral("cancelDeleteTextButton")}
                    confirmTextButton={tGeneral("confirmDeleteTextButton")}
                    confirmDialogFunction={() => removeMvcInvoiceFromList(rowIndex)}
                    Button={
                        <IconButton size='small' edge="end">
                            <DeleteIcon />
                        </IconButton>
                    }
                />
            );
        else
            return (<></>)
    }

   
    async function AddMvcInvoiceToList() {
        if (circularLoading)
            return false;
            
        setCircularLoading(true);
        var invoiceKeySearch = refFieldInvoiceKey.current
        if (!invoiceKeySearch)
        {
            setCircularLoading(false);
            return
        }

        var invoiceKey = String(invoiceKeySearch.value)

        if (invoiceKey.length != 44) {
            enqueueSnackbar(t("invoiceKeyNot44CharValidation"), { variant: 'warning' })
            setCircularLoading(false);
            return
        }

        var existingInvoice = false
        fields.map((field) => {
            if ((field as any).invoiceKey === invoiceKey){
            existingInvoice = true
            }
        })

        if (existingInvoice) {
            enqueueSnackbar(t("mvcInvoiceOnList"), { variant: 'warning' })
            setCircularLoading(false)
            return
        }

        var validInvoice = true
        await MvcApi.validateInvoicesInUse({
            mvcId: (Mvc?.id ?? 0),
            invoiceKeys: [ invoiceKey ]
          })
        .catch((err) => {
            setCircularLoading(false)
            validInvoice = false
        })

        if(!validInvoice) {
            return
        }

        let mvcInvoiceTemp: any;
        
        await MvcApi.getInvoiceData((Mvc?.id ?? 0), (Mvc?.companyId ?? 0), user.company.plantID, invoiceKey).then(res => { 
            mvcInvoiceTemp = res.data;
        })
        .catch(() => {setCircularLoading(false)})

        //So adicionar na lista caso saiba o tipo da nota. Senao, usuario deve escolher
        if (mvcInvoiceTemp.invoiceType !== null)
        {
            addInvoiceToList(mvcInvoiceTemp)
        }
        else 
        {
            setUnknownTypeInvoice(mvcInvoiceTemp)
            setSelectInvoiceType(true)
        }

        invoiceKeySearch.value = ""

        if (onAdd && mvcInvoiceTemp && mvcInvoiceTemp.invoiceSupplierId)
            onAdd({ 
                code: mvcInvoiceTemp.invoiceSupplierId.toString(), 
                label: mvcInvoiceTemp.invoiceSupplierId.toString() + ' - '  + mvcInvoiceTemp.invoiceSupplierCorporateName, 
                supplierCorporateName: mvcInvoiceTemp.invoiceSupplierCorporateName, 
                uf: mvcInvoiceTemp.supplierUf })

        setCircularLoading(false)
    }

    const removeMvcInvoiceFromList = (index: number) => {
        remove(index)
    }

    //Adicionar nota fiscal na lista da grid:
    function addInvoiceToList(mvcInvoice:any) {
        mvcInvoice.id = 0; //garantir que id = 0 (nova NF inserida na lista): Se a nf veio do elog, pode ter vindo com o id_nota_Fiscal preenchido neste campo erroneamente
        append(mvcInvoice);
        enqueueSnackbar(t("mvcInvoiceAddSuccess"), { variant: 'success' });
    }

    //Usuario escolheu o tipo da nota fiscal:
    async function handleChooseInvoiceType(invoiceType: string) {
        let mvcInvoice = unknownTypeInvoice;

        if (invoiceType === 'cancel')
        {
            setSelectInvoiceType(false);
            setUnknownTypeInvoice(null);
            return;
        }

        //Preencher o tipo e incluir na lista
        if (mvcInvoice !== null)
        {
            mvcInvoice.invoiceType = invoiceType;
            addInvoiceToList(mvcInvoice);
            setSelectInvoiceType(false);
        }
    }

    return (
        <Grid style={{ marginTop: 20, width: "98%", marginLeft: 10, marginBottom: 10 }}>
            <Typography className={classes.root} style={{ marginLeft: 0 }} >
                {title ? title : t("invoices")}
            </Typography>

            {!allowAdd ? <Grid /> :
                <Grid container spacing={1} style={{ marginBottom: 10 }}>
                    <Grid item >
                        <TextField
                            label={t("invoiceKey")}
                            name="invoiceKeySearch"
                            type='text'
                            onKeyPress={(e) => {
                                if (e.key === 'Enter') {
                                    AddMvcInvoiceToList()
                                    refFieldInvoiceKey.current?.focus()
                                }
                            }}
                            onInput={(e) => { (e.target as HTMLInputElement).value = (e.target as HTMLInputElement).value.slice(0, 44) }}
                            style={{ width: 400 }}
                            inputRef={refFieldInvoiceKey} />
                    </Grid>

                    <Grid>
                        <ButtonUI
                            onClick={() => { AddMvcInvoiceToList(); refFieldInvoiceKey.current?.focus()} }
                            color="primary"
                            variant="contained"
                            style={{ maxWidth: '40px', maxHeight: '38px', minWidth: '40px', minHeight: '38px', marginTop: 15, marginLeft: '10px' }}>
                            <AddIcon />
                        </ButtonUI>
                    </Grid>
                </Grid>
            }

            <Backdrop style={{ zIndex: 9999 }} open={circularLoading}>
                <CircularProgress color="inherit" />
            </Backdrop>
            <Typography className={classes.paper} style={{ marginLeft: 0 }} >
                {tGeneral("totalRecords")} {fields ? fields.length : '0'}
            </Typography>
            <MuiThemeProvider theme={myTheme} >
                <MUIDataTable
                    title=""
                    data={fields}
                    columns={columns}
                    options={{
                        ...getDefaultGridOptions(tGeneral),
                        serverSide: false,
                        pagination: false,
                        sort: false,
                        selectToolbarPlacement: "none",
                        filter: false,
                        search: false,
                        download: false,
                        print: false,
                        viewColumns: false,
                        selectableRows: 'none',
                    }}
                />
            </MuiThemeProvider>

            
            {/* Dialog de selecao do tipo da NF */ }
            <Dialog open={selectInvoiceType} scroll="body">
                <DialogTitle>{it("unknownInvoiceTypeTitle")}</DialogTitle>
                <DialogContent><DialogContentText>{it("unknownInvoiceTypeText")}</DialogContentText></DialogContent>
                <List>
                    <ListItem button key={'cancel'} onClick={() => handleChooseInvoiceType('cancel')}>
                        <ListItemAvatar>
                            <Avatar>
                            <DeleteIcon />
                            </Avatar>
                        </ListItemAvatar>
                        <ListItemText>{it("unknownInvoiceTypeCancel")}</ListItemText>
                    </ListItem>
                    {invoiceTypes.map((x) => {
                        return (
                            <ListItem button key={x} onClick={() => handleChooseInvoiceType(x)}>
                                <ListItemAvatar>
                                    <Avatar>
                                    <AddIcon />
                                    </Avatar>
                                </ListItemAvatar>
                                <ListItemText>{it("invoiceType_" + x)}</ListItemText>
                            </ListItem>
                        );
                    })}
                </List>
            </Dialog>
        </Grid>
    )
}

export default MvcInvoiceList